import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import Button from '../Button';
import Spinner from '../Spinner';
import { getAsset } from '../../../util/assets';
import useOpenModal from '../../../hooks/useOpenModal';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { formatPriceForDisplay } from '../../../util/format-money';
import { STRIPE_CHECKOUT_MODAL } from '../StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { getErrorString } from '../../../util/normalize-errors';
import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import useEventAddonPurchase from '../../../apps/pro/components/FreemiumEventPurchase/hooks/useEventAddonPurchase';
import { MeQuery } from '../../../graphql/queries/account';
import { EVENT_ADDON_ITEM_UPSELL_META } from './helpers';
import EventAddonItemInformationModal from '../../../apps/pro/components/FreemiumEventPurchase/components/EventAddonItemInformationModal';
import EventAddonItemFeatures from '../../../apps/pro/components/FreemiumEventUpgrade/components/EventAddonItemFeatures';
import WaldofiedFeatureComparisonModal, {
  WALDOFIED_FEATURE_COMPARISON,
} from '../../../apps/pro/components/FreemiumEventPurchase/components/WaldofiedFeatureComparisonModal';
import ArrowRight from '../../icons/ArrowRight';

const propTypes = {
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string.isRequired,
  albumSubtype: PropTypes.string,
  onCheckoutSuccess: PropTypes.func,
  eventAddonItemType: PropTypes.string.isRequired,
  featureId: PropTypes.string,
};

const EventAddonItemUpsell = ({
  albumId,
  albumFolderId,
  albumSubtype,
  onCheckoutSuccess,
  eventAddonItemType,
  featureId,
}) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const { data: { me: account } = {}, loading } = useQuery(MeQuery);

  const meta = EVENT_ADDON_ITEM_UPSELL_META(albumSubtype)[eventAddonItemType];

  const {
    onCheckout,
    submitting,
    eventAddonItems,
    selectedEventAddonItemIds,
    onEventAddonItemToggle,
    loadingEventAddonItems,
  } = useEventAddonPurchase({
    albumId,
    albumFolderId,
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    ],
    onCheckoutError: ({ error }) => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: () => {
      onCheckoutSuccess?.();
    },
  });

  const upsoldEventAddon = (eventAddonItems ?? []).find(
    ({ types }) => types[0] === eventAddonItemType,
  );

  useEffect(() => {
    if (
      upsoldEventAddon?.id &&
      !selectedEventAddonItemIds.includes(upsoldEventAddon.id)
    ) {
      onEventAddonItemToggle(upsoldEventAddon.id);
    }
  }, [upsoldEventAddon]);

  const onPurchase = () => {
    openModal(STRIPE_CHECKOUT_MODAL, {
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_addon_items: [
            {
              album_folder_id: albumFolderId,
              event_addon_item_id: upsoldEventAddon?.id,
            },
          ],
          promo_code: promoCode,
        };
      },
      accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
      onSuccess: token =>
        onCheckout({
          token,
          albumFolderId,
        }),
      defaultName: account?.name ?? '',
      defaultEmail: account?.email_address ?? '',
    });
  };

  if (loading || loadingEventAddonItems) {
    return (
      <div className="d-flex align-items-center justify-content-center w-100 h-100">
        <Spinner />
      </div>
    );
  }

  if (!meta) {
    return null;
  }

  const isWaldofiedEvent =
    eventAddonItemType === EVENT_ADDON_ITEM_TYPE.WALDOFIED;

  const featureMeta = EVENT_ADDON_ITEM_UPSELL_META(albumSubtype)
    .EVENT_ADDON_ITEM_FEATURE_UPSELL_META[featureId];

  if (featureId && featureMeta) {
    return (
      <div className="d-flex flex-column align-items-center">
        <img className="mb-1" src={featureMeta.illustration} alt="Diamond" />
        <h2 className="text-center">
          {featureMeta.title ?? 'Upgrade your event!'}
        </h2>
        <p className="text-dark text-center">{featureMeta.subtitle}</p>
        <Button
          className="mt-1"
          onClick={onPurchase}
          disabled={submitting || !selectedEventAddonItemIds?.length}
          {...(featureMeta.purchaseBtnProps ?? {})}
        >
          {submitting ? (
            'Processing...'
          ) : (
            <Fragment>
              {isWaldofiedEvent ? 'Upgrade to Gold' : 'Buy now'}
              {upsoldEventAddon
                ? ` ($${formatPriceForDisplay(upsoldEventAddon.price.value)})`
                : ''}
            </Fragment>
          )}
        </Button>
        {isWaldofiedEvent && (
          <Button
            btnSize="sm"
            btnStyle="link"
            className="mb-1"
            onClick={() => openModal(WALDOFIED_FEATURE_COMPARISON)}
          >
            Compare with free{' '}
            <ArrowRight color="#0d84fe" size={12} className="ml-1" />
          </Button>
        )}
        <WaldofiedFeatureComparisonModal
          waldofiedEventAddonItem={upsoldEventAddon}
          eventType={albumSubtype}
        />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center">
      <img
        className="mb-1"
        width="48px"
        height="48px"
        src={getAsset('/diamond-orange.svg')}
        alt="Diamond"
      />
      <h2 className="text-center">{meta.title ?? 'Upgrade your event!'}</h2>
      <p className="text-dark text-center">{meta.subtitle}</p>
      {meta?.features?.length > 0 && (
        <EventAddonItemFeatures features={meta?.features} />
      )}
      <Button
        className="mt-1"
        onClick={onPurchase}
        disabled={submitting || !selectedEventAddonItemIds?.length}
      >
        {submitting ? (
          'Processing...'
        ) : (
          <Fragment>
            {isWaldofiedEvent ? 'Upgrade to Gold' : 'Buy now'}
            {upsoldEventAddon
              ? ` ($${formatPriceForDisplay(upsoldEventAddon.price.value)})`
              : ''}
          </Fragment>
        )}
      </Button>
      {isWaldofiedEvent && (
        <Button
          btnSize="sm"
          btnStyle="link"
          className="mt-1"
          onClick={() => openModal(WALDOFIED_FEATURE_COMPARISON)}
        >
          Compare with free{' '}
          <ArrowRight color="#0d84fe" size={12} className="ml-1" />
        </Button>
      )}
      {meta.howItWorks && (
        <>
          <Button
            btnStyle="link"
            onClick={() => {
              setHowItWorksOpen(true);
            }}
            className="mt-1"
          >
            See how it works
          </Button>
          <EventAddonItemInformationModal
            forceOpen={howItWorksOpen}
            onClose={() => setHowItWorksOpen(false)}
            title="How it works"
            {...meta.howItWorks}
          />
        </>
      )}
      <EventAddonItemInformationModal />
      <WaldofiedFeatureComparisonModal
        waldofiedEventAddonItem={upsoldEventAddon}
        eventType={albumSubtype}
      />
    </div>
  );
};

EventAddonItemUpsell.propTypes = propTypes;

export default EventAddonItemUpsell;
